import { MakeLogicType, actions, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'

import api from 'lib/api'
import { lemonToast } from 'lib/lemon-ui/LemonToast'

export interface DevUser {
    email: string
    first_name: string
    is_staff: boolean
    label: string | null
    last_login: string | null
}

export const DEV_LOGIN_SECONDS_SAVED_PER_CLICK = 5

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface devLoginLogicValues {
    createFreshAccountLoading: boolean
    devLoginCount: number
    devLoginPanelOpen: boolean
    devLoginTimeSavedLabel: string | null
    devUsers: DevUser[]
    devUsersLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface devLoginLogicActions {
    createFreshAccount: () => {
        value: true
    }
    createFreshAccountFailure: (error: string) => {
        error: string
    }
    createFreshAccountSuccess: () => {
        value: true
    }
    devLogin: (email: string) => {
        email: string
    }
    loadDevUsers: (_: any) => any
    loadDevUsersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDevUsersSuccess: (
        devUsers: DevUser[],
        payload?: any
    ) => {
        devUsers: DevUser[]
        payload?: any
    }
    setDevLoginPanelOpen: (open: boolean) => {
        open: boolean
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface devLoginLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        devLoginTimeSavedLabel: (devLoginCount: number) => string | null
    }
}

export type devLoginLogicType = MakeLogicType<
    devLoginLogicValues,
    devLoginLogicActions,
    Record<string, any>,
    devLoginLogicMeta
>

export const devLoginLogic = kea<devLoginLogicType>([
    path(['scenes', 'authentication', 'shared', 'devLoginLogic']),
    actions({
        devLogin: (email: string) => ({ email }),
        createFreshAccount: true,
        createFreshAccountSuccess: true,
        createFreshAccountFailure: (error: string) => ({ error }),
        setDevLoginPanelOpen: (open: boolean) => ({ open }),
    }),
    reducers({
        devLoginCount: [
            0,
            { persist: true },
            {
                devLogin: (count) => count + 1,
            },
        ],
        // Open out of the box — the panel is the fastest way into a local instance. Persisted so
        // that closing it sticks for anyone who'd rather type credentials.
        devLoginPanelOpen: [
            true,
            { persist: true },
            {
                setDevLoginPanelOpen: (_, { open }) => open,
            },
        ],
        createFreshAccountLoading: [
            false,
            {
                createFreshAccount: () => true,
                createFreshAccountSuccess: () => false,
                createFreshAccountFailure: () => false,
            },
        ],
    }),
    loaders(() => ({
        devUsers: [
            [] as DevUser[],
            {
                // Not fired on an `onMount` because we don't always need it. The whole list comes
                // down in one go — this only ever runs against a local instance.
                loadDevUsers: async (_, breakpoint) => {
                    breakpoint()
                    try {
                        // nosemgrep: prefer-codegen-api -- Legacy raw API call with a hand-written URL and an unchecked response type. No generated function covers this endpoint yet. Find out why the generated client skips it (no schema, no product tag, or excluded from the spec) and fix that first.
                        const response = await api.get<{ users: DevUser[] }>('api/login/dev')
                        breakpoint()
                        return response.users
                    } catch {
                        // Endpoint is unavailable unless allow_dev_login is set in preflight.
                        return []
                    }
                },
            },
        ],
    })),
    selectors(() => ({
        devLoginTimeSavedLabel: [
            (s) => [s.devLoginCount],
            (devLoginCount: number): string | null => {
                if (devLoginCount === 0) {
                    return null
                }

                const totalSeconds = devLoginCount * DEV_LOGIN_SECONDS_SAVED_PER_CLICK
                if (totalSeconds < 60) {
                    const unit = totalSeconds === 1 ? 'second' : 'seconds'
                    return `You've saved ${totalSeconds} ${unit} by clicking this button.`
                }

                const minutes = Math.floor(totalSeconds / 60)
                const unit = minutes === 1 ? 'minute' : 'minutes'
                return `You've saved ${minutes} ${unit} by clicking this button.`
            },
        ],
    })),
    listeners(({ actions }) => ({
        devLogin: async ({ email }) => {
            // Dynamic import to avoid a circular dependency: loginLogic statically imports this logic.
            const { loginLogic, redirectAfterLogin } = await import('scenes/authentication/login/loginLogic')
            loginLogic.actions.clearGeneralError()
            try {
                // nosemgrep: prefer-codegen-api -- Legacy raw API call with a hand-written URL and an unchecked response type. No generated function covers this endpoint yet. Find out why the generated client skips it (no schema, no product tag, or excluded from the spec) and fix that first.
                await api.create<any>('api/login/dev', { email })
            } catch (e) {
                const { code, detail } = e as Record<string, any>
                loginLogic.actions.setGeneralError(code || 'dev_login_failed', detail || 'Dev login failed')
                return
            }
            redirectAfterLogin()
        },
        createFreshAccount: async () => {
            try {
                // nosemgrep: prefer-codegen-api -- Legacy raw API call with a hand-written URL and an unchecked response type. No generated function covers this endpoint yet. Find out why the generated client skips it (no schema, no product tag, or excluded from the spec) and fix that first.
                await api.create<any>('api/login/dev', { create_fresh_account: true })
            } catch (e) {
                actions.createFreshAccountFailure((e as Record<string, any>).detail || 'Failed to create account')
                lemonToast.error('Failed to create fresh account')
                return
            }
            actions.createFreshAccountSuccess()
            // Fresh accounts always land on onboarding; skip login redirect handling.
            window.location.href = '/onboarding'
        },
    })),
])
